/**
 * DualCalendarPicker
 * A drop-in replacement for <input type="date"> that supports both
 * Gregorian (ميلادي) and Hijri (هجري) calendars.
 *
 * Props:
 *   value       – YYYY-MM-DD string (always Gregorian, same as <input type="date">)
 *   onChange    – (value: 'YYYY-MM-DD') => void
 *   min / max   – YYYY-MM-DD optional bounds
 *   language    – 'ar' | 'en'
 *   className   – forwarded to the trigger button
 *   style       – forwarded to the trigger button
 *   placeholder – shown when value is empty
 *   disabled    – boolean
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toHijri, toGregorian } from 'hijri-converter';

// ─── helpers ────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

/** YYYY-MM-DD → { y, m, d } (all numbers) */
const parseGreg = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
};

/** { y, m, d } → YYYY-MM-DD */
const formatGreg = ({ y, m, d }) => `${y}-${pad(m)}-${pad(d)}`;

/** Gregorian YYYY-MM-DD → Hijri { y, m, d } */
const gregToHijri = (str) => {
  const g = parseGreg(str);
  if (!g) return null;
  const h = toHijri(g.y, g.m, g.d);
  return { y: h.hy, m: h.hm, d: h.hd };
};

/** Hijri { y, m, d } → Gregorian YYYY-MM-DD */
const hijriToGreg = ({ y, m, d }) => {
  const g = toGregorian(y, m, d);
  return formatGreg({ y: g.gy, m: g.gm, d: g.gd });
};

/** Days in a Gregorian month */
const gregDaysInMonth = (y, m) => new Date(y, m, 0).getDate();

/** Approximate days in a Hijri month (14-step lookup) */
const hijriDaysInMonth = (hy, hm) => {
  // Odd months have 30 days, even months 29, except month 12 in leap years (30)
  // Hijri leap year: year % 30 in {2,5,7,10,13,16,18,21,24,26,29}
  const leapYears = new Set([2, 5, 7, 10, 13, 16, 18, 21, 24, 26, 29]);
  if (hm === 12) return leapYears.has(hy % 30) ? 30 : 29;
  return hm % 2 === 1 ? 30 : 29;
};

/** 0 = Sunday, from JS Date */
const gregWeekdayOf = (y, m, d) => new Date(y, m - 1, d).getDay();

/** Gregorian month names (short) */
const GREG_MONTHS_EN = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const GREG_MONTHS_AR = [
  'يناير','فبراير','مارس','أبريل','مايو','يونيو',
  'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'
];

/** Hijri month names */
const HIJRI_MONTHS_AR = [
  'محرم','صفر','ربيع الأول','ربيع الثاني',
  'جمادى الأولى','جمادى الآخرة','رجب','شعبان',
  'رمضان','شوال','ذو القعدة','ذو الحجة'
];
const HIJRI_MONTHS_EN = [
  'Muharram','Safar','Rabi al-Awwal','Rabi al-Thani',
  'Jumada al-Ula','Jumada al-Akhira','Rajab','Shaban',
  'Ramadan','Shawwal','Dhul Qadah','Dhul Hijjah'
];

const WEEKDAYS_AR = ['أح','إث','ثل','أر','خم','جم','سب'];
const WEEKDAYS_EN = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// ─── display helpers ─────────────────────────────────────────────────────────

/** Format a Gregorian YYYY-MM-DD for display */
const displayGreg = (str, language) => {
  const g = parseGreg(str);
  if (!g) return '';
  const months = language === 'ar' ? GREG_MONTHS_AR : GREG_MONTHS_EN;
  return language === 'ar'
    ? `${g.d} ${months[g.m - 1]} ${g.y}`
    : `${months[g.m - 1]} ${g.d}, ${g.y}`;
};

/** Format a Gregorian YYYY-MM-DD as Hijri for display */
const displayHijri = (str, language) => {
  const h = gregToHijri(str);
  if (!h) return '';
  const months = language === 'ar' ? HIJRI_MONTHS_AR : HIJRI_MONTHS_EN;
  return language === 'ar'
    ? `${h.d} ${months[h.m - 1]} ${h.y} هـ`
    : `${h.d} ${months[h.m - 1]} ${h.y} AH`;
};

// ─── sub-components ──────────────────────────────────────────────────────────

/** Gregorian calendar grid */
const GregGrid = ({ viewY, viewM, selected, onSelect, min, max, language }) => {
  const days = gregDaysInMonth(viewY, viewM);
  const startWd = gregWeekdayOf(viewY, viewM, 1);
  const cells = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  const minG = parseGreg(min);
  const maxG = parseGreg(max);
  const selG = parseGreg(selected);

  const isDisabled = (d) => {
    if (!d) return true;
    const cur = { y: viewY, m: viewM, d };
    const curStr = formatGreg(cur);
    if (minG && curStr < min) return true;
    if (maxG && curStr > max) return true;
    return false;
  };
  const isSelected = (d) => {
    if (!d || !selG) return false;
    return selG.y === viewY && selG.m === viewM && selG.d === d;
  };
  const isToday = (d) => {
    if (!d) return false;
    const t = new Date();
    return t.getFullYear() === viewY && (t.getMonth() + 1) === viewM && t.getDate() === d;
  };

  const wds = language === 'ar' ? WEEKDAYS_AR : WEEKDAYS_EN;

  return (
    <div style={{ userSelect: 'none' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px', marginBottom: '4px' }}>
        {wds.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#94a3b8', padding: '4px 0' }}>{w}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
        {cells.map((d, i) => {
          const disabled = isDisabled(d);
          const selected = isSelected(d);
          const today = isToday(d);
          return (
            <button
              key={i}
              type="button"
              disabled={disabled || !d}
              onClick={() => d && !disabled && onSelect(formatGreg({ y: viewY, m: viewM, d }))}
              style={{
                width: '32px', height: '32px', borderRadius: '8px', border: 'none',
                background: selected ? '#f97316' : today ? '#fff7ed' : 'transparent',
                color: !d ? 'transparent' : selected ? '#fff' : disabled ? '#cbd5e1' : today ? '#ea580c' : '#1e293b',
                fontWeight: selected || today ? 700 : 400,
                fontSize: '13px', cursor: d && !disabled ? 'pointer' : 'default',
                outline: 'none', transition: 'background 0.1s',
              }}
            >
              {d || ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Hijri calendar grid */
const HijriGrid = ({ viewHY, viewHM, selected, onSelect, min, max, language }) => {
  const days = hijriDaysInMonth(viewHY, viewHM);
  // Determine starting weekday: convert 1st of this Hijri month to Gregorian → get weekday
  const firstGreg = hijriToGreg({ y: viewHY, m: viewHM, d: 1 });
  const startWd = firstGreg ? gregWeekdayOf(...firstGreg.split('-').map(Number)) : 0;

  const cells = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  // Convert selected / min / max Gregorian strings to Hijri for comparison
  const selH = gregToHijri(selected);
  const minH = min ? gregToHijri(min) : null;
  const maxH = max ? gregToHijri(max) : null;

  const hijriCmp = (h) => h.y * 10000 + h.m * 100 + h.d;

  const isDisabled = (d) => {
    if (!d) return true;
    const cur = { y: viewHY, m: viewHM, d };
    if (minH && hijriCmp(cur) < hijriCmp(minH)) return true;
    if (maxH && hijriCmp(cur) > hijriCmp(maxH)) return true;
    return false;
  };
  const isSelected = (d) => {
    if (!d || !selH) return false;
    return selH.y === viewHY && selH.m === viewHM && selH.d === d;
  };
  // Today in Hijri
  const todayG = new Date();
  const todayH = toHijri(todayG.getFullYear(), todayG.getMonth() + 1, todayG.getDate());
  const isToday = (d) => d && todayH.hy === viewHY && todayH.hm === viewHM && todayH.hd === d;

  const wds = language === 'ar' ? WEEKDAYS_AR : WEEKDAYS_EN;

  return (
    <div style={{ userSelect: 'none' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px', marginBottom: '4px' }}>
        {wds.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#94a3b8', padding: '4px 0' }}>{w}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
        {cells.map((d, i) => {
          const disabled = isDisabled(d);
          const sel = isSelected(d);
          const today = isToday(d);
          return (
            <button
              key={i}
              type="button"
              disabled={disabled || !d}
              onClick={() => {
                if (!d || disabled) return;
                const greg = hijriToGreg({ y: viewHY, m: viewHM, d });
                onSelect(greg);
              }}
              style={{
                width: '32px', height: '32px', borderRadius: '8px', border: 'none',
                background: sel ? '#f97316' : today ? '#fff7ed' : 'transparent',
                color: !d ? 'transparent' : sel ? '#fff' : disabled ? '#cbd5e1' : today ? '#ea580c' : '#1e293b',
                fontWeight: sel || today ? 700 : 400,
                fontSize: '13px', cursor: d && !disabled ? 'pointer' : 'default',
                outline: 'none', transition: 'background 0.1s',
              }}
            >
              {d || ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const DualCalendarPicker = ({
  value = '',
  onChange,
  min,
  max,
  language = 'ar',
  className = '',
  style = {},
  placeholder,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [calType, setCalType] = useState('greg'); // 'greg' | 'hijri'
  const containerRef = useRef(null);

  // Gregorian view state
  const todayJs = new Date();
  const initG = parseGreg(value) || { y: todayJs.getFullYear(), m: todayJs.getMonth() + 1, d: todayJs.getDate() };
  const [viewGY, setViewGY] = useState(initG.y);
  const [viewGM, setViewGM] = useState(initG.m);

  // Hijri view state
  const todayH = toHijri(todayJs.getFullYear(), todayJs.getMonth() + 1, todayJs.getDate());
  const initH = gregToHijri(value) || { y: todayH.hy, m: todayH.hm, d: todayH.hd };
  const [viewHY, setViewHY] = useState(initH.y);
  const [viewHM, setViewHM] = useState(initH.m);

  // Sync view when value changes externally
  useEffect(() => {
    if (value) {
      const g = parseGreg(value);
      if (g) { setViewGY(g.y); setViewGM(g.m); }
      const h = gregToHijri(value);
      if (h) { setViewHY(h.y); setViewHM(h.m); }
    }
  }, [value]);

  // Close on outside click
  const handleOutside = useCallback((e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open, handleOutside]);

  const handleSelect = (gregStr) => {
    onChange && onChange(gregStr);
    setOpen(false);
  };

  // Navigation helpers
  const prevGMonth = () => {
    if (viewGM === 1) { setViewGY((y) => y - 1); setViewGM(12); }
    else setViewGM((m) => m - 1);
  };
  const nextGMonth = () => {
    if (viewGM === 12) { setViewGY((y) => y + 1); setViewGM(1); }
    else setViewGM((m) => m + 1);
  };
  const prevHMonth = () => {
    if (viewHM === 1) { setViewHY((y) => y - 1); setViewHM(12); }
    else setViewHM((m) => m - 1);
  };
  const nextHMonth = () => {
    if (viewHM === 12) { setViewHY((y) => y + 1); setViewHM(1); }
    else setViewHM((m) => m + 1);
  };

  // Display label on trigger button
  const gregLabel = value ? displayGreg(value, language) : null;
  const hijriLabel = value ? displayHijri(value, language) : null;
  const defaultPlaceholder = language === 'ar' ? 'اختر تاريخاً' : 'Select date';

  const gregMonths = language === 'ar' ? GREG_MONTHS_AR : GREG_MONTHS_EN;
  const hijriMonths = language === 'ar' ? HIJRI_MONTHS_AR : HIJRI_MONTHS_EN;

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={className}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: '8px', padding: '6px 12px',
          fontSize: '13px', color: value ? '#1e293b' : '#94a3b8',
          textAlign: language === 'ar' ? 'right' : 'left',
          minWidth: '180px', whiteSpace: 'nowrap',
          ...style
        }}
      >
        <span style={{ fontSize: '14px' }}>📅</span>
        <span style={{ flex: 1 }}>
          {value ? (
            <span>
              <span style={{ fontWeight: 600 }}>{gregLabel}</span>
              {hijriLabel && (
                <span style={{ fontSize: '11px', color: '#64748b', marginRight: language === 'ar' ? 0 : '6px', marginLeft: language === 'ar' ? '6px' : 0 }}>
                  {' '}({hijriLabel})
                </span>
              )}
            </span>
          ) : (placeholder || defaultPlaceholder)}
        </span>
        <span style={{ color: '#94a3b8', fontSize: '10px' }}>▼</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          [language === 'ar' ? 'right' : 'left']: 0,
          zIndex: 9999,
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '14px',
          boxShadow: '0 8px 32px rgba(15,23,42,0.14)',
          padding: '16px',
          minWidth: '280px',
        }}>
          {/* Calendar type tabs */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
            {[
              { key: 'greg',  arLabel: 'ميلادي', enLabel: 'Gregorian' },
              { key: 'hijri', arLabel: 'هجري',   enLabel: 'Hijri' },
            ].map(({ key, arLabel, enLabel }) => (
              <button
                key={key}
                type="button"
                onClick={() => setCalType(key)}
                style={{
                  flex: 1, padding: '6px 10px', borderRadius: '8px', border: 'none',
                  background: calType === key ? '#f97316' : '#f1f5f9',
                  color: calType === key ? '#fff' : '#64748b',
                  fontWeight: calType === key ? 700 : 500,
                  fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {language === 'ar' ? arLabel : enLabel}
              </button>
            ))}
          </div>

          {calType === 'greg' ? (
            <>
              {/* Gregorian header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <button type="button" onClick={prevGMonth} style={navBtnStyle}>‹</button>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <select
                    value={viewGM}
                    onChange={(e) => setViewGM(Number(e.target.value))}
                    style={selectStyle}
                  >
                    {gregMonths.map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={viewGY}
                    onChange={(e) => setViewGY(Number(e.target.value))}
                    style={{ ...selectStyle, width: '64px' }}
                  />
                </div>
                <button type="button" onClick={nextGMonth} style={navBtnStyle}>›</button>
              </div>
              <GregGrid
                viewY={viewGY} viewM={viewGM}
                selected={value} onSelect={handleSelect}
                min={min} max={max} language={language}
              />
            </>
          ) : (
            <>
              {/* Hijri header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <button type="button" onClick={prevHMonth} style={navBtnStyle}>‹</button>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <select
                    value={viewHM}
                    onChange={(e) => setViewHM(Number(e.target.value))}
                    style={selectStyle}
                  >
                    {hijriMonths.map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={viewHY}
                    onChange={(e) => setViewHY(Number(e.target.value))}
                    style={{ ...selectStyle, width: '64px' }}
                  />
                </div>
                <button type="button" onClick={nextHMonth} style={navBtnStyle}>›</button>
              </div>
              <HijriGrid
                viewHY={viewHY} viewHM={viewHM}
                selected={value} onSelect={handleSelect}
                min={min} max={max} language={language}
              />
            </>
          )}

          {/* Selected value footer */}
          {value && (
            <div style={{
              marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f1f5f9',
              fontSize: '12px', color: '#64748b', textAlign: 'center'
            }}>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{gregLabel}</span>
              {hijriLabel && (
                <span> · <span style={{ color: '#f97316' }}>{hijriLabel}</span></span>
              )}
              <button
                type="button"
                onClick={() => { onChange && onChange(''); setOpen(false); }}
                style={{ marginInlineStart: '10px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '11px' }}
              >
                ✕ {language === 'ar' ? 'مسح' : 'Clear'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const navBtnStyle = {
  background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px',
  width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#64748b', lineHeight: 1, padding: 0,
};

const selectStyle = {
  border: '1px solid #e2e8f0', borderRadius: '6px',
  padding: '3px 6px', fontSize: '13px', color: '#1e293b',
  background: '#f8fafc', cursor: 'pointer',
};

export default DualCalendarPicker;
